import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { drawerGuides } from "@/data/learnGuides";

// Grouped drawer nav, 'Shop' collects every buy-something destination, 'Trust &
// info' collects everything researchers open when they're evaluating us. The
// four items in the top nav (Pen Kits, Raw Vials, Bundles, Best Sellers) are
// intentionally NOT repeated here, the drawer's job is to expose the deep
// items that got cut from the top row, not to re-list what's already visible.
const SHOP = [
  { label: "All Peptides", to: "/peptides" },
  { label: "Pen Kits", to: "/pen-kits" },
  { label: "Raw Vials", to: "/raw-vials" },
  { label: "Bundles", to: "/bundles" },
  { label: "Accessories", to: "/accessories" },
  { label: "Blends", to: "/blends" },
];
const TRUST = [
  { label: "About", to: "/about" },
  { label: "Authenticity", to: "/authenticity" },
  { label: "Verify a Box", to: "/verify" },
  { label: "COA Library", to: "/coa" },
  { label: "Contact", to: "/contact" },
];
// LEARN groups educational research-guide pages. Kept short deliberately,
// the /learn hub is the discoverability surface as the library grows. Only
// guides with `drawer: true` in learnGuides.js appear here; every published
// guide remains reachable through /learn, /peptides Research Guides, footer
// links and search. To promote/demote a guide from the drawer, toggle its
// `drawer` flag in src/data/learnGuides.js, no drawer changes needed.
const LEARN = [
  { label: "Research Guides", to: "/learn" },
  ...drawerGuides().map((g) => ({
    // Existing drawer capitalisation used "What is …?" / "What are …?"; the
    // metadata titles use "What Is / Are". Lower-case both auxiliary verbs
    // so drawer test-ids stay stable (drawer-learn-what-is-nad, etc.).
    label: g.title.replace(/^What Is /, "What is ").replace(/^What Are /, "What are "),
    to: `/learn/${g.slug}`,
  })),
];

export default function MobileDrawer({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[60] transition-opacity ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      data-testid="mobile-drawer"
      aria-hidden={!open}
    >
      <div
        className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="mobile-drawer-overlay"
      />
      <aside
        className={`absolute left-0 top-0 flex h-full w-[min(360px,88vw)] flex-col bg-white shadow-2xl transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <span className="font-mono text-[11px] tracking-[0.22em] text-subtle">MENU</span>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-ink hover:bg-line/60"
            aria-label="Close menu"
            data-testid="mobile-drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <p className="label-mono">§ Shop</p>
          <ul className="mt-3 space-y-3">
            {SHOP.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  onClick={onClose}
                  className="block font-heading text-lg font-bold text-ink hover:text-brandblue"
                  data-testid={`drawer-shop-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="label-mono mt-8">§ Learn</p>
          <ul className="mt-3 space-y-3">
            {LEARN.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  onClick={onClose}
                  className="block font-heading text-lg font-bold text-ink hover:text-brandblue"
                  data-testid={`drawer-learn-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="label-mono mt-8">§ Trust &amp; info</p>
          <ul className="mt-3 space-y-3">
            {TRUST.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  onClick={onClose}
                  className="block font-heading text-lg font-bold text-ink hover:text-brandblue"
                  data-testid={`drawer-trust-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
